import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n/TranslationContext';

const navKeys = [
  { key: 'nav.home', href: '#home' },
  { key: 'nav.about', href: '#about' },
  { key: 'nav.services', href: '#services' },
  { key: 'nav.creators', href: '/creators' },
  { key: 'nav.brands', href: '/brands' },
  { key: 'nav.contact', href: '#contact' },
];

const languages = [
  { code: 'en', labelKey: 'lang.en' },
  { code: 'ko', labelKey: 'lang.ko' },
  { code: 'zh', labelKey: 'lang.zh' },
] as const;

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-xl border-b border-foreground-100/60'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        <a href="#home" className="flex items-center gap-2.5 cursor-pointer flex-shrink-0">
          <img
            src="/logo.png"
            alt="Dragonfruit"
            className="h-7 w-auto"
          />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navKeys.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/85 hover:text-background-50'
              }`}
            >
              {t(item.key)}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div ref={langRef} className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'bg-foreground-100/70 text-foreground-700 hover:bg-foreground-200/70'
                  : 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
              }`}
            >
              {t(currentLang.labelKey)}
              <i className={`text-[10px] ${langOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
            </button>
            {langOpen && (
              <div className="absolute top-full right-0 mt-1.5 bg-background-50 rounded-xl border border-foreground-100/60 shadow-lg overflow-hidden py-1 min-w-[100px]">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      lang.code === i18n.language
                        ? 'text-foreground-950 font-semibold bg-foreground-50'
                        : 'text-foreground-600 hover:bg-foreground-50'
                    }`}
                  >
                    {t(lang.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="#contact"
            className={`hidden md:flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'bg-foreground-950 text-background-50 hover:bg-foreground-800'
                : 'bg-background-50 text-foreground-950 hover:bg-background-200'
            }`}
          >
            {t('nav.contact')}
            <i className="ri-arrow-right-up-line text-sm"></i>
          </a>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${scrolled ? 'text-foreground-950' : 'text-background-50'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-foreground-100/60 px-4 py-5 flex flex-col gap-1">
          {navKeys.map((item) => (
            <a
              key={item.key}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="px-2 py-3 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-foreground-50 rounded-lg transition-colors cursor-pointer"
            >
              {t(item.key)}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setMobileOpen(false)}
            className="mt-3 px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm font-medium text-center cursor-pointer"
          >
            {t('nav.contact')}
          </a>
        </div>
      )}
    </nav>
  );
}